<template>
    <div class="qr-scanner-container">
        <v-btn @click="toggleCamera" :color="showCamera ? 'error' : 'primary'" :disabled="isLoading || isProcessing">
            {{ showCamera ? 'Annulla' : 'Scansiona codice' }}
        </v-btn>

        <!-- Messaggio di caricamento iniziale -->
        <div v-if="isLoading" class="loading-badge">
            <v-icon small color="info">mdi-loading mdi-spin</v-icon>
            <span>Caricamento in corso...</span>
        </div>

        <!-- <v-btn @click="registraTimbratura('Battisti|1768399395|1768399395');">TEST</v-btn> -->
        <!-- Indicatore log in attesa -->
        <!--         <div v-if="pendingLogsCount > 0" class="logs-pending-badge">
            <v-icon small color="error">mdi-file-document</v-icon>
            <span>{{ pendingLogsCount }} log in attesa di sincronizzazione</span>
        </div> -->

        <!-- Indicatore timbrature offline in attesa -->
        <div v-if="pendingOfflineCount > 0" class="offline-pending-badge">
            <v-icon small color="warning">mdi-cloud-upload</v-icon>
            <span>{{ pendingOfflineCount }} timbratur{{ pendingOfflineCount > 1 ? 'e' : 'a' }} in attesa di
                sincronizzazione. Collegati a una rete e i dati verranno sincronizzati automaticamente.</span>
        </div>

        <!-- Riquadro sempre visibile -->
        <div class="camera-container">
            <div class="video-wrapper">
                <video v-show="showCamera && !isLoading" ref="videoPreview" id="videoPreview"></video>
                <div v-if="!showCamera && !isProcessing" class="placeholder-message">
                    <div class="placeholder-content">
                        <v-icon size="48" color="grey lighten-1">mdi-qrcode-scan</v-icon>
                        <p>Clicca su "<strong>Scansiona codice</strong>" per avviare la
                            scansione del QR code.</p>
                    </div>
                </div>

                <div v-if="isProcessing" class="processing-overlay">
                    <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
                </div>
                <div v-if="successFeedback" class="success-overlay"></div>
            </div>
            <p v-if="scanningError" class="error-message">{{ scanningError }}</p>
        </div>

        <p class="result-message">
            <span v-html="datiRegistrati"></span>
        </p>

        <v-row justify="center" class="mt-4">
            <v-col cols="12" sm="6">
                <v-btn block large :color="entrataUscita === 'E' ? 'success' : 'gray'"
                    @click="salvaTimbratura('entrata')" :disabled="isLoading || isProcessing">
                    <v-icon left>login</v-icon>
                    Entrata
                </v-btn>
            </v-col>

            <v-col cols="12" sm="6">
                <v-btn block large :color="entrataUscita === 'U' ? 'success' : 'gray'"
                    @click="salvaTimbratura('uscita')" :disabled="isLoading || isProcessing">
                    <v-icon left>logout</v-icon>
                    Uscita
                </v-btn>
            </v-col>
        </v-row>

    </div>
</template>

<script>
import { BrowserMultiFormatReader } from '@zxing/library';
import { valida, getStatus, validaOffline, logTimbraturaEvent } from "@/api/api.timbratura.js"

const STORAGE_KEY = 'timbrature_offline';
const LOG_STORAGE_KEY = 'timbratura_logs_offline';
const SESSION_ID_KEY = 'timbratura_session_id';

export default {
    data() {
        return {
            qrResult: null,
            codeReader: new BrowserMultiFormatReader(),
            showCamera: false,
            scanningError: null,
            successFeedback: false,
            isProcessing: false,
            datiRegistrati: "",

            // Gestione offline
            isOnline: navigator.onLine,
            pendingOfflineCount: 0,
            syncInterval: null,
            isSyncing: false,

            entrataUscita: 'E',
            isLoading: true,
            ultimaTimbratura: null,

            // Gestione logging
            sessionId: null,
            pendingLogsCount: 0,
            logSyncInterval: null,
            isLogSyncing: false
        };
    },

    async mounted() {
        this.showCamera = false;
        this.initSessionId();
        this.loadUltimaTimbratura();
        this.calcolaEApplicaSuggerimento();

        this.logEvent('APP_START', {
            userAgent: navigator.userAgent,
            online: this.isOnline,
            hasBadge: !!this.codiceBadge
        });

        await new Promise(resolve => setTimeout(resolve, 200));
        if (this.isOnline) {
            try {
                await this.getStato();
            } catch (error) {
                console.warn('Impossibile recuperare stato da server, uso logica locale:', error);
                this.logEvent('GET_STATUS_ERROR', { error: error.message });
            }
        }

        await new Promise(resolve => setTimeout(resolve, 300));
        this.isLoading = false;
        await this.checkAndOpenCamera();
        this.initOfflineManagement();
        this.initLogManagement();
    },

    computed: {
        nomeDipendente() {
            return this.$store.getters["user/getNomeDipendente"];
        },
        codiceBadge() {
            return this.$store.getters["user/getCodiceBadge"];
        },
    },

    watch: {
        async codiceBadge(newVal) {
            if (newVal) {
                this.loadUltimaTimbratura();
                this.calcolaEApplicaSuggerimento();
                await this.getStato();

                // Avvia la sincronizzazione solo dopo che Vuex ha caricato i dati utente
                if (this.isOnline) {
                    this.startSyncProcess();
                }
            }
        },
        async nomeDipendente(newVal) {
            if (newVal) {
                this.loadUltimaTimbratura();
                this.calcolaEApplicaSuggerimento();
                await this.getStato();
            }
        }
    },

    methods: {
        //#region ========== UTILITIES & COSTANTI ==========

        // Genera un UUID v4 per l'Idempotency Key
        generateUUID() {
            return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
                const r = Math.random() * 16 | 0;
                const v = c === 'x' ? r : (r & 0x3 | 0x8);
                return v.toString(16);
            });
        },

        getTimeInMinutes(hours, minutes) {
            return hours * 60 + minutes;
        },

        // LOGICA CORRETTA (Sincronizzata col C#): Adesso valuta l'ultima timbratura
        calcolaEntrataUscitaSuggerita(ultima, ora) {
            const oraMinuti = ora.getHours() * 60 + ora.getMinutes();

            const ingressoMattinoEnd = this.getTimeInMinutes(9, 15);
            const uscitaPranzoStart = this.getTimeInMinutes(11, 45);
            const uscitaPranzoEnd = this.getTimeInMinutes(13, 15);
            const ingressoPomeriggioEnd = this.getTimeInMinutes(14, 15);
            const uscitaSeraStart = this.getTimeInMinutes(16, 45);

            // Se non c'è una timbratura precedente oggi
            if (!ultima || !ultima.EntrataUscita) {
                return oraMinuti < uscitaPranzoStart ? "E" : "U";
            }

            const ultimoTipo = ultima.EntrataUscita;

            if (ultimoTipo === "E") {
                if (oraMinuti >= uscitaPranzoStart && oraMinuti <= uscitaPranzoEnd) return "U";
                if (oraMinuti >= uscitaSeraStart) return "U";
                if (oraMinuti <= ingressoMattinoEnd) return "E"; // Tolleranza
                return "U";
            }

            if (ultimoTipo === "U") {
                if (oraMinuti <= ingressoMattinoEnd) return "E";
                if (oraMinuti >= uscitaPranzoStart && oraMinuti <= ingressoPomeriggioEnd) return "E";
                if (oraMinuti >= uscitaSeraStart) return "U";
                return "E";
            }

            return "E";
        },

        saveUltimaTimbratura(entrataUscita, timestamp) {
            try {
                const userKey = `ultima_timbratura_${this.codiceBadge}`;
                const data = { EntrataUscita: entrataUscita, Timestamp: timestamp };
                localStorage.setItem(userKey, JSON.stringify(data));
                this.ultimaTimbratura = data;
            } catch (error) {
                console.error('Errore salvataggio:', error);
            }
        },

        loadUltimaTimbratura() {
            try {
                const userKey = `ultima_timbratura_${this.codiceBadge}`;
                const data = localStorage.getItem(userKey);
                this.ultimaTimbratura = data ? JSON.parse(data) : null;
                return this.ultimaTimbratura;
            } catch (error) {
                return null;
            }
        },

        calcolaEApplicaSuggerimento() {
            const ora = new Date();
            const suggerimento = this.calcolaEntrataUscitaSuggerita(this.ultimaTimbratura, ora);
            this.entrataUscita = suggerimento;
            return suggerimento;
        },
        //#endregion

        //#region ========== CORE TIMBRATURA (OFFLINE-FIRST) ==========

        async registraTimbratura(qrContent) {
            const currentTimestamp = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, -1);
            const messageId = this.generateUUID(); // Idempotency Key
            const tipoSelezionato = this.entrataUscita;

            this.logEvent('TIMBRATURA_START', { qrContent: qrContent ? qrContent.substring(0, 20) : null, tipo: tipoSelezionato });

            // 1. OFFLINE-FIRST: Salvo IMMEDIATAMENTE in localStorage. 
            const offlineTimbratura = {
                clientMessageId: messageId,
                timestamp: currentTimestamp,
                qrContent: qrContent,
                nomeDipendente: this.nomeDipendente,
                codiceBadge: this.codiceBadge,
                entrataUscita: tipoSelezionato
            };
            this.saveOfflineTimbratura(offlineTimbratura);

            // Aggiorno la UI locale per calcoli futuri
            this.saveUltimaTimbratura(tipoSelezionato, currentTimestamp);
            this.calcolaEApplicaSuggerimento();

            // 2. AGGIORNAMENTO UI ISTANTANEO (Simulazione Offline)
            const dateObj = new Date(currentTimestamp);
            const ore = String(dateObj.getHours()).padStart(2, '0');
            const minuti = String(dateObj.getMinutes()).padStart(2, '0');
            const tipoTesto = tipoSelezionato === 'E' ? 'entrata' : 'uscita';

            // Imposto un messaggio temporaneo che l'utente vede subito. 
            // Se non c'è rete, questo è il messaggio che rimarrà a schermo.
            this.datiRegistrati = `<div style="color: #856404; font-weight: bold;">
                ${tipoTesto.charAt(0).toUpperCase() + tipoTesto.slice(1)} registrata sul dispositivo alle ore ${ore}:${minuti}. <br>
                <small>(In attesa di rete per la sincronizzazione)</small>
            </div>`;

            if (!this.isOnline) {
                return;
            }

            // 3. TENTATIVO ONLINE (con Timeout a 3 secondi)
            try {
                let par = {
                    ClientMessageId: messageId,
                    Token: qrContent,
                    IdStazione: "Battisti",
                    Fingerprint: this.getDeviceFingerprint(),
                    NomeDipendente: this.nomeDipendente,
                    CodiceBadge: this.codiceBadge,
                    EntrataUscita: tipoSelezionato
                };

                let res = await valida(par);

                // 4. SUCCESSO API
                if (res && res.data && res.data.success) {
                    this.removeOfflineTimbraturaById(messageId); // Rimuovo dalla coda
                    // Sovrascrivo il messaggio temporaneo con quello definitivo in verde del server
                    this.datiRegistrati = `<div style="color: green; font-weight: bold;">${res.data.message}</div>`;
                    this.getStato();
                    this.logEvent('TIMBRATURA_ONLINE_SUCCESS', { tipo: tipoSelezionato });

                    if (this.pendingOfflineCount > 0) {
                        this.syncOfflineTimbrature();
                    }
                } else {
                    // Errore logico dal server
                    this.removeOfflineTimbraturaById(messageId);
                    this.datiRegistrati = `<div style="color: red; font-weight: bold;">Errore: ${res.data ? res.data.message : 'Errore sconosciuto'}</div>`;
                }

            } catch (error) {
                this.logEvent('TIMBRATURA_NETWORK_ERROR', { error: error.message });
                this.handleOffline(); // Forza lo stato offline
                // In caso di errore di rete, il messaggio "Attesa di rete" impostato al punto 2 rimane correttamente a schermo.
            }
        },

        mostraMessaggioOffline() {
            this.datiRegistrati = `
                <div style="background-color: #fff3cd; padding: 15px; border-radius: 8px; border-left: 4px solid #ffc107;">
                    <strong style="color: #856404;">⚠ Connessione Assente o Lenta</strong><br/>
                    <span style="color: #856404;">
                        Timbratura salvata sul tuo telefono.<br/>
                        Puoi chiudere l'app, sarà sincronizzata automaticamente in background.
                    </span>
                </div>
            `;
        },

        //#endregion

        //#region ========== GESTIONE OFFLINE & CODE ==========

        getOfflineTimbrature() {
            try {
                const data = localStorage.getItem(STORAGE_KEY);
                return data ? JSON.parse(data) : [];
            } catch (error) { return []; }
        },

        saveOfflineTimbratura(timbratura) {
            const timbrature = this.getOfflineTimbrature();
            timbrature.push(timbratura);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(timbrature));
            this.updatePendingCount();
        },

        removeOfflineTimbraturaById(id) {
            let timbrature = this.getOfflineTimbrature();
            timbrature = timbrature.filter(t => t.clientMessageId !== id);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(timbrature));
            this.updatePendingCount();
        },

        clearOfflineTimbrature() {
            localStorage.removeItem(STORAGE_KEY);
            this.updatePendingCount();
        },

        updatePendingCount() {
            this.pendingOfflineCount = this.getOfflineTimbrature().length;
        },

        async syncOfflineTimbrature() {
            if (this.isSyncing || !this.isOnline) return;

            const timbrature = this.getOfflineTimbrature();
            if (timbrature.length === 0) {
                this.stopSyncProcess();
                return;
            }
            this.logEvent('SYNC_PROCESS_STARTED', { queueLength: timbrature.length });
            this.isSyncing = true;

            const failsafeTimer = setTimeout(() => {
                this.isSyncing = false;
            }, 30000);
            timbrature.sort((a, b) => a.timestamp - b.timestamp);

            let successCount = 0;
            let failedTimbrature = [];

            for (const timbratura of timbrature) {
                timbratura.retryCount = (timbratura.retryCount || 0) + 1;
                if (timbratura.retryCount > 10) {
                    this.logEvent('OFFLINE_SYNC_DROPPED', { id: timbratura.clientMessageId });
                    continue; // Se fallisce 10 volte, la scarto per non bloccare la coda
                }
                try {
                    const par = {
                        ClientMessageId: timbratura.clientMessageId,
                        Timestamp: timbratura.timestamp,
                        Token: timbratura.qrContent,
                        NomeDipendente: timbratura.nomeDipendente || this.nomeDipendente,
                        CodiceBadge: timbratura.codiceBadge || this.codiceBadge,
                        IdStazione: "Battisti",
                        Fingerprint: this.getDeviceFingerprint(),
                        EntrataUscita: timbratura.entrataUscita,
                    };

                    let res = await validaOffline(par);

                    // Se la richiesta va a buon fine O riceve un errore di logica dal server,
                    // non dobbiamo riprovare in futuro. Incrementiamo success e non la pushiamo in failed.
                    successCount++;
                    await new Promise(resolve => setTimeout(resolve, 100));

                } catch (error) {
                    // Errore di Rete: salviamo per riprovare
                    failedTimbrature.push(timbratura);
                }
            }

            clearTimeout(failsafeTimer);

            if (failedTimbrature.length > 0) {
                this.logEvent('SYNC_PROCESS_PARTIAL_FAIL', { failedCount: failedTimbrature.length });
                localStorage.setItem(STORAGE_KEY, JSON.stringify(failedTimbrature));
            } else {
                this.logEvent('SYNC_PROCESS_SUCCESS');
                this.clearOfflineTimbrature();
                this.stopSyncProcess();
            }

            this.updatePendingCount();
            this.isSyncing = false;
        },

        //#endregion

        //#region ========== CAMERA E SCANSIONE ==========

        async checkAndOpenCamera() {
            if (this.isLoading) return;
            try {
                const devices = await navigator.mediaDevices.enumerateDevices();
                const hasCamera = devices.some(device => device.kind === 'videoinput');
                if (hasCamera) await this.openCamera();
            } catch (err) { console.error(err); }
        },

        async toggleCamera() {
            if (this.isLoading) return;
            this.showCamera ? this.closeCamera() : await this.openCamera();
        },

        async openCamera() {
            this.showCamera = true;
            this.qrResult = null;
            this.scanningError = null;
            try {
                await this.codeReader.decodeFromVideoDevice(
                    undefined,
                    this.$refs.videoPreview,
                    (result, err) => {
                        if (result) {
                            this.isProcessing = true;
                            this.qrResult = result.text;
                            this.successFeedback = true;
                            // Feedback Aptico (vibrazione) all'utente
                            if (navigator.vibrate) navigator.vibrate(200);
                            setTimeout(() => this.successFeedback = false, 1500);
                            this.registraTimbratura(result.text);
                            this.isProcessing = false;
                            this.closeCamera();
                        }
                    },
                    { delayBetweenScanAttempts: 100, delayBetweenScanSuccess: 500 }
                );
            } catch (err) {
                this.scanningError = "Impossibile accedere alla fotocamera: " + err.message;
                this.showCamera = false;
                this.isProcessing = false;
            }
        },

        closeCamera() {
            this.codeReader.reset();
            this.showCamera = false;
        },

        //#endregion

        //#region ========== LOGGING & INFRASTRUTTURA BASE ==========

        salvaTimbratura(tipo) {
            const vecchioTipo = this.entrataUscita;
            const nuovoTipo = tipo === 'entrata' ? "E" : "U";
            if (vecchioTipo === nuovoTipo) return;

            this.entrataUscita = nuovoTipo;
            this.logEvent('CAMBIO_MANUALE_TIPOLOGIA', { da: vecchioTipo, a: nuovoTipo });
        },

        async getStato() {
            try {
                let par = {
                    IdStazione: "Battisti",
                    Fingerprint: this.getDeviceFingerprint(),
                    NomeDipendente: this.nomeDipendente,
                    CodiceBadge: this.codiceBadge,
                };
                let res = await getStatus(par);

                // Aggiornamento col nuovo contratto JSON
                if (res.data && res.data.message) {
                    this.datiRegistrati = res.data.message;
                    if (res.data.entrataUscita) this.entrataUscita = res.data.entrataUscita;
                }
            } catch (error) {
                console.error('Errore stato:', error);
            }
        },

        getDeviceFingerprint() {
            return { userAgent: window.navigator.userAgent, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone };
        },

        initSessionId() {
            let sessionId = localStorage.getItem(SESSION_ID_KEY);
            if (!sessionId) {
                sessionId = this.generateUUID();
                localStorage.setItem(SESSION_ID_KEY, sessionId);
            }
            this.sessionId = sessionId;
        },

        logEvent(type, details = {}) {
            try {
                const logEntry = {
                    sessionId: this.sessionId,
                    timestamp: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, -1),
                    eventType: type,
                    codiceBadge: this.codiceBadge,
                    nomeDipendente: this.nomeDipendente,
                    isOnline: this.isOnline,
                    details: details
                };
                if (this.isOnline) {
                    logTimbraturaEvent(logEntry).catch(() => this.saveLogOffline(logEntry));
                } else {
                    this.saveLogOffline(logEntry);
                }
            } catch (e) {
                console.error("Error logging event:", e);
            }
        },

        saveLogOffline(logEntry) {
            const logs = this.getOfflineLogs();
            logs.push(logEntry);
            localStorage.setItem(LOG_STORAGE_KEY, JSON.stringify(logs));
        },

        getOfflineLogs() {
            const data = localStorage.getItem(LOG_STORAGE_KEY);
            return data ? JSON.parse(data) : [];
        },

        initOfflineManagement() {
            this.updatePendingCount();
            window.addEventListener('online', this.handleOnline);
            window.addEventListener('offline', this.handleOffline);
            //if (this.isOnline) this.startSyncProcess();
        },

        handleOnline() {
            this.isOnline = true;
            this.startSyncProcess();
            this.syncOfflineLogs();
        },

        handleOffline() {
            this.isOnline = false;
            this.stopSyncProcess();
        },

        startSyncProcess() {
            this.syncOfflineTimbrature();
            if (!this.syncInterval) {
                this.syncInterval = setInterval(() => this.syncOfflineTimbrature(), 60000);
            }
        },

        stopSyncProcess() {
            if (this.syncInterval) {
                clearInterval(this.syncInterval);
                this.syncInterval = null;
            }
        },

        initLogManagement() {
            this.pendingLogsCount = this.getOfflineLogs().length;
            // Tenta il sync dei log 5 secondi dopo il caricamento per non bloccare il main thread all'avvio
            setTimeout(() => {
                if (this.isOnline) this.syncOfflineLogs();
            }, 5000);
        },

        async syncOfflineLogs() {
            if (this.isLogSyncing || !this.isOnline) return;

            let logs = this.getOfflineLogs();
            if (logs.length === 0) return;

            this.isLogSyncing = true;
            let failedLogs = [];

            // Processa i log dal più vecchio al più recente
            for (const log of logs) {
                try {
                    // Usa la funzione API esistente
                    await logTimbraturaEvent(log);
                } catch (error) {
                    failedLogs.push(log);
                }
            }

            if (failedLogs.length > 0) {
                localStorage.setItem(LOG_STORAGE_KEY, JSON.stringify(failedLogs));
            } else {
                localStorage.removeItem(LOG_STORAGE_KEY);
            }

            this.pendingLogsCount = failedLogs.length;
            this.isLogSyncing = false;
        }

        //#endregion
    },

    // Utilizzo della sintassi Vue 2 corretta
    beforeDestroy() {
        this.closeCamera();
        window.removeEventListener('online', this.handleOnline);
        window.removeEventListener('offline', this.handleOffline);
        this.stopSyncProcess();
    },
};
</script>

<style scoped>
.qr-scanner-container {
    margin-top: 2em;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
}

.offline-pending-badge {
    display: flex;
    align-items: center;
    gap: 8px;
    background-color: #fff3cd;
    color: #856404;
    padding: 10px 20px;
    border-radius: 8px;
    border: 1px solid #ffc107;
    font-weight: 500;
    animation: pulse-warning 2s infinite;
}

.loading-badge {
    display: flex;
    align-items: center;
    gap: 8px;
    background-color: #e3f2fd;
    color: #1976d2;
    padding: 10px 20px;
    border-radius: 8px;
    border: 1px solid #90caf9;
    font-weight: 500;
}

.logs-pending-badge {
    display: flex;
    align-items: center;
    gap: 8px;
    background-color: #ffebee;
    color: #c62828;
    padding: 10px 20px;
    border-radius: 8px;
    border: 1px solid #ef9a9a;
    font-weight: 500;
}

@keyframes pulse-warning {

    0%,
    100% {
        opacity: 1;
    }

    50% {
        opacity: 0.8;
    }
}

.camera-container {
    margin-top: 10px;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.video-wrapper {
    position: relative;
    width: 300px;
    height: 300px;
    border: 2px solid #ccc;
    border-radius: 12px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
}

video {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.processing-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(255, 255, 255, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
}

.success-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 255, 0, 0.3);
    border-radius: 12px;
    pointer-events: none;
}

.error-message {
    color: red;
    margin-top: 10px;
    margin-left: 8px;
    margin-right: 8px;
}

.result-message {
    margin-top: 10px;
    max-width: 500px;
    text-align: center;
    margin-left: 4px;
    margin-right: 4px;
}

.placeholder-message {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(245, 245, 245, 0.9);
    border-radius: 12px;
    text-align: center;
    padding: 20px;
}

.placeholder-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
}

.placeholder-content p {
    margin: 0;
    color: #666;
    font-size: 16px;
}
</style>